import {
  ROTULO_DO_STATUS_DE_MULTA,
  STATUS_DE_MULTA,
  type StatusDeMulta,
  formatarPlaca,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CalendarClock,
  CircleDollarSign,
  CircleSlash,
  EllipsisVertical,
  Inbox,
  Link2,
  type LucideIcon,
  Plus,
  ReceiptText,
  Scale,
  TriangleAlert,
  UserCheck,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { diasAte } from '@/features/frota/api'
import {
  DIAS_DE_ALERTA_DO_PRAZO,
  type Multa,
  type StatusDeMultaAtualizavel,
  dataDaquiADias,
  indicacaoPendente,
  referenciaDaReserva,
  useAtualizarStatusDaMulta,
  useCobrarMultaDoCliente,
  useFrotaParaSelecao,
  useMultas,
} from '@/features/multas/api'
import { DialogoDeIndicacaoDeCondutor, DialogoDeMulta } from '@/features/multas/dialogo-de-multa'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR, formatarData, formatarDataHora } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'

/**
 * Multas de trânsito — Gap 4.
 *
 * ---------------------------------------------------------------------------
 * ESTA LISTA É UMA FILA DE URGÊNCIA, NÃO UM ARQUIVO
 * ---------------------------------------------------------------------------
 * A coluna que decide é o PRAZO de indicação: perdê-lo dobra a multa para a
 * locadora (NIC). Por isso a ordenação inicial é por prazo, o filtro traz o
 * atalho "vencendo em 7 dias" e o alerta vermelho só aparece enquanto a
 * indicação AINDA está pendente — multa cobrada com prazo vencido é história,
 * não urgência.
 *
 * ---------------------------------------------------------------------------
 * A VINCULAÇÃO NÃO SE EDITA AQUI
 * ---------------------------------------------------------------------------
 * A coluna de reserva é leitura: quem vincula é a API, por placa + período.
 * Uma multa "— não vinculada" se resolve conferindo o registro (placa errada,
 * data errada), nunca digitando um número de reserva.
 *
 * O amarelo aparece uma vez: no CTA "Registrar multa". Alerta de prazo é
 * `danger`, nunca a cor da marca.
 */

const TAMANHO_DA_PAGINA = 20

/**
 * Cor, ícone e rótulo no MESMO registro — cor nunca comunica sozinha, e dois
 * mapas paralelos compilariam com metade da resposta. O `satisfies` obriga os
 * seis status.
 */
const APRESENTACAO_DO_STATUS = {
  RECEBIDA: { icone: Inbox, variante: 'neutral' },
  VINCULADA: { icone: Link2, variante: 'info' },
  CONDUTOR_INDICADO: { icone: UserCheck, variante: 'success' },
  COBRANCA_AGENDADA: { icone: CalendarClock, variante: 'info' },
  COBRADA: { icone: CircleDollarSign, variante: 'success' },
  CONTESTADA: { icone: Scale, variante: 'warning' },
  CANCELADA: { icone: CircleSlash, variante: 'neutral' },
} as const satisfies Readonly<
  Record<StatusDeMulta, { readonly icone: LucideIcon; readonly variante: BadgeProps['variant'] }>
>

/** Por onde a multa foi cobrada, em palavras. */
const ROTULO_DA_VIA = {
  FECHAMENTO: 'no fechamento',
  CAUCAO: 'descontada da caução',
  CARTAO: 'no cartão (provedor)',
  MANUAL: 'manualmente',
} as const

function BadgeDeStatusDaMulta({ status }: { readonly status: StatusDeMulta }): ReactElement {
  const { icone: Icone, variante } = APRESENTACAO_DO_STATUS[status]

  return (
    <Badge variant={variante}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_MULTA[status]}
    </Badge>
  )
}

/**
 * O prazo com o alarme junto. O vermelho só liga enquanto a indicação está
 * pendente E faltam `DIAS_DE_ALERTA_DO_PRAZO` dias ou menos — e nunca é só
 * cor: o rótulo diz "vence em N dias", e o tooltip diz a consequência (NIC).
 */
function rotuloDoVencimento(dias: number): string {
  if (dias < 0) {
    return 'Prazo vencido'
  }

  if (dias === 0) {
    return 'Vence hoje'
  }

  return `Vence em ${String(dias)} dia${dias === 1 ? '' : 's'}`
}

function CelulaDePrazo({ multa }: { readonly multa: Multa }): ReactElement {
  const dias = diasAte(multa.prazoParaIndicacao)

  return (
    <div className="min-w-32 space-y-1">
      <span className="block whitespace-nowrap text-sm tabular-money text-fg-secondary">
        {formatarData(multa.prazoParaIndicacao)}
      </span>
      {dias !== null && dias <= DIAS_DE_ALERTA_DO_PRAZO && indicacaoPendente(multa) ? (
        <Tooltip>
          <TooltipTrigger
            className="cursor-help rounded-md focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-ring"
          >
            <Badge variant="danger">
              <TriangleAlert aria-hidden="true" />
              {rotuloDoVencimento(dias)}
            </Badge>
          </TooltipTrigger>
          <TooltipContent className="max-w-64">
            Perder o prazo de indicação dobra a multa para a locadora (multa NIC). Indique o
            condutor antes do vencimento.
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  )
}

export function MultasPage(): ReactElement {
  const podeCriar = usePermissao('traffic_fine.create')
  const podeAtualizar = usePermissao('traffic_fine.update')

  const [pagina, setPagina] = useState(1)
  const [status, setStatus] = useState<StatusDeMulta | ''>('')
  const [prazoAte, setPrazoAte] = useState('')

  const [dialogoDeRegistro, setDialogoDeRegistro] = useState(false)
  const [multaParaIndicar, setMultaParaIndicar] = useState<Multa | null>(null)

  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, status, prazoAte }),
    [pagina, status, prazoAte],
  )

  const consulta = useMultas(filtros)
  const frota = useFrotaParaSelecao()
  const atualizarStatus = useAtualizarStatusDaMulta()
  const cobrarDoCliente = useCobrarMultaDoCliente()

  function cobrar(multa: Multa): void {
    cobrarDoCliente.mutate(multa.id, {
      onSuccess: (cobranca) => {
        toast.success(`Multa ${multa.autoDeInfracao}`, {
          description:
            cobranca.destino === 'CAUCAO'
              ? `Descontada da caução retida: ${formatarMoedaDoWire(cobranca.total)} (taxa de ${formatarMoedaDoWire(cobranca.taxaAdministrativa)} incluída).`
              : `Cliente avisado. A cobrança de ${formatarMoedaDoWire(cobranca.total)} no cartão acontece em ${formatarDataHora(cobranca.cobrancaEm)}.`,
        })
      },
      // O 409 da API diz o caminho ("cobre por fora e marque cobrada") — a tela
      // repassa a mensagem dela.
      onError: (causa: unknown) => {
        toast.error('Não foi possível cobrar a multa', { description: errorMessage(causa) })
      },
    })
  }

  /**
   * A resposta de multas traz a placa, mas não o modelo — e placa sozinha não
   * diz nada para quem atende o telefone. O modelo vem da frota já carregada
   * para o diálogo de registro; se a frota falhar, a linha degrada para a
   * placa sozinha em vez de quebrar a lista.
   */
  const modeloPorVeiculo = useMemo(() => {
    const mapa = new Map<string, string>()

    for (const veiculo of frota.data?.itens ?? []) {
      mapa.set(veiculo.id, veiculo.modelo)
    }

    return mapa
  }, [frota.data])

  const temFiltro = status !== '' || prazoAte !== ''

  function limparFiltros(): void {
    setStatus('')
    setPrazoAte('')
    setPagina(1)
  }

  function mudarStatus(multa: Multa, novo: StatusDeMultaAtualizavel): void {
    atualizarStatus.mutate(
      { id: multa.id, status: novo },
      {
        onSuccess: (salva) => {
          toast.success(`Multa ${salva.autoDeInfracao} atualizada`, {
            description: `Agora está como “${ROTULO_DO_STATUS_DE_MULTA[salva.status]}”.`,
          })
        },
        // O 422 da API (transição que o serviço recusa) chega com a mensagem
        // dela — a tela repassa em vez de traduzir errado.
        onError: (causa: unknown) => {
          toast.error('Não foi possível atualizar a multa', { description: errorMessage(causa) })
        },
      },
    )
  }

  const colunas = useMemo<ColumnDef<Multa>[]>(
    () => [
      {
        id: 'auto',
        header: 'Auto de infração',
        accessorFn: (linha: Multa) => linha.autoDeInfracao,
        cell: ({ row }) => {
          const multa: Multa = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-semibold tracking-wide tabular-money text-foreground">
                {multa.autoDeInfracao}
              </p>
              <p className="truncate text-xs text-fg-secondary">{multa.orgaoAutuador}</p>
            </div>
          )
        },
      },
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha: Multa) => linha.veiculoPlaca,
        cell: ({ row }) => {
          const multa: Multa = row.original
          const modelo = modeloPorVeiculo.get(multa.vehicleId)

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap font-medium tabular-money text-foreground">
                {formatarPlaca(multa.veiculoPlaca)}
              </p>
              {modelo === undefined ? null : (
                <p className="truncate text-xs text-fg-secondary">{modelo}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'dataDaInfracao',
        header: 'Infração em',
        accessorFn: (linha: Multa) => linha.dataDaInfracao,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm tabular-money text-fg-secondary">
            {formatarDataHora(row.original.dataDaInfracao)}
          </span>
        ),
      },
      {
        id: 'valor',
        header: 'Valor',
        meta: { alinhamento: 'direita' },
        // O valor é string de centavos: ordenar lexicograficamente mentiria
        // ("900" > "10000"), e convertê-lo para number só para ordenar seria o
        // primeiro number a tocar em dinheiro neste arquivo.
        enableSorting: false,
        cell: ({ row }) => (
          <span className="money-cell block min-w-24 text-foreground">
            {formatarMoedaDoWire(row.original.valor)}
          </span>
        ),
      },
      {
        id: 'pontos',
        header: 'Pontos',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Multa) => linha.pontos,
        cell: ({ row }) => (
          <span className="block min-w-16 text-right tabular-money text-foreground">
            {String(row.original.pontos)}
          </span>
        ),
      },
      {
        id: 'prazo',
        header: 'Prazo de indicação',
        accessorFn: (linha: Multa) => linha.prazoParaIndicacao,
        cell: ({ row }) => <CelulaDePrazo multa={row.original} />,
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const multa: Multa = row.original
          return (
            <div className="min-w-0 space-y-1">
              <BadgeDeStatusDaMulta status={multa.status} />
              {multa.status === 'COBRANCA_AGENDADA' && multa.cobrancaAgendadaPara !== null ? (
                <span className="block text-xs text-fg-secondary">
                  no cartão em {formatarDataHora(multa.cobrancaAgendadaPara)}
                </span>
              ) : null}
              {multa.status === 'COBRADA' && multa.cobradaVia !== null ? (
                <span className="block text-xs text-fg-secondary">
                  {ROTULO_DA_VIA[multa.cobradaVia]}
                  {multa.taxaAdministrativa === null || multa.taxaAdministrativa === '0'
                    ? ''
                    : ` · taxa ${formatarMoedaDoWire(multa.taxaAdministrativa)}`}
                </span>
              ) : null}
              {multa.erroDaCobranca === null ? null : (
                <span className="flex items-start gap-1 text-xs text-danger-fg">
                  <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden="true" />
                  {multa.erroDaCobranca} Cobre manualmente.
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'reserva',
        header: 'Reserva',
        enableSorting: false,
        cell: ({ row }) => {
          const multa: Multa = row.original

          if (multa.reservationId === null) {
            return <span className="whitespace-nowrap text-xs text-fg-muted">— não vinculada</span>
          }

          return (
            <div className="min-w-0">
              <span
                className="block whitespace-nowrap text-sm tabular-money text-foreground"
                title={multa.reservationId}
              >
                {referenciaDaReserva(multa.reservationId)}
              </span>
              {multa.condutorNome === null ? null : (
                <span className="block truncate text-xs text-fg-secondary">
                  {multa.condutorNome}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const multa: Multa = row.original

          if (!podeAtualizar) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          // Indicar exige reserva vinculada; reindicação corrige um engano
          // enquanto a multa ainda não foi cobrada. As transições manuais só
          // aparecem quando mudariam alguma coisa — e a API é quem recusa de
          // verdade, com 422 que vira toast.
          const podeIndicar =
            multa.reservationId !== null &&
            (multa.status === 'VINCULADA' || multa.status === 'CONDUTOR_INDICADO')
          const podeCobrar =
            multa.status !== 'COBRADA' &&
            multa.status !== 'CANCELADA' &&
            multa.status !== 'COBRANCA_AGENDADA'
          // Cobrar DO CLIENTE: multa vinculada que chegou depois do fechamento —
          // a API decide entre a caução retida e o cartão guardado no provedor.
          const podeCobrarDoCliente =
            multa.reservationId !== null &&
            (multa.status === 'VINCULADA' || multa.status === 'CONDUTOR_INDICADO')
          const podeContestar = multa.status !== 'CONTESTADA' && multa.status !== 'CANCELADA'
          const podeCancelar = multa.status !== 'CANCELADA'

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações da multa ${multa.autoDeInfracao}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                {podeIndicar ? (
                  <>
                    <DropdownMenuItem
                      onSelect={() => {
                        setMultaParaIndicar(multa)
                      }}
                    >
                      <UserCheck aria-hidden="true" />
                      Indicar condutor
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}

                {podeCobrarDoCliente ? (
                  <DropdownMenuItem
                    disabled={cobrarDoCliente.isPending}
                    onSelect={() => {
                      cobrar(multa)
                    }}
                  >
                    <CircleDollarSign aria-hidden="true" />
                    Cobrar do cliente
                  </DropdownMenuItem>
                ) : null}

                {podeCobrar ? (
                  <DropdownMenuItem
                    // Uma escrita por vez: sem isto o segundo clique dispararia
                    // outra requisição e outro toast dizendo a mesma coisa.
                    disabled={atualizarStatus.isPending}
                    onSelect={() => {
                      mudarStatus(multa, 'COBRADA')
                    }}
                  >
                    <CircleDollarSign aria-hidden="true" />
                    Marcar cobrada
                  </DropdownMenuItem>
                ) : null}

                {podeContestar ? (
                  <DropdownMenuItem
                    disabled={atualizarStatus.isPending}
                    onSelect={() => {
                      mudarStatus(multa, 'CONTESTADA')
                    }}
                  >
                    <Scale aria-hidden="true" />
                    Marcar contestada
                  </DropdownMenuItem>
                ) : null}

                {podeCancelar ? (
                  <DropdownMenuItem
                    disabled={atualizarStatus.isPending}
                    onSelect={() => {
                      mudarStatus(multa, 'CANCELADA')
                    }}
                  >
                    <CircleSlash aria-hidden="true" />
                    Cancelar multa
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda
    // o conjunto de colunas são a permissão, a escrita em curso e o mapa de
    // modelos da frota.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeAtualizar, atualizarStatus.isPending, modeloPorVeiculo],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Multas de trânsito</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            A notificação entra como chegou do órgão e o sistema vincula a reserva pela placa e pelo
            período. O relógio que importa é o prazo de indicação — perdê-lo dobra a multa.
          </p>
        </div>

        {podeCriar ? (
          <Button
            onClick={() => {
              setDialogoDeRegistro(true)
            }}
          >
            <Plus aria-hidden="true" />
            Registrar multa
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-status-multas">Status</Label>
          <Select
            id="filtro-status-multas"
            value={status}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setStatus(STATUS_DE_MULTA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos</option>
            {STATUS_DE_MULTA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_STATUS_DE_MULTA[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-prazo-multas">Prazo de indicação até</Label>
          <div className="flex items-center gap-2">
            <Input
              id="filtro-prazo-multas"
              type="date"
              value={prazoAte}
              onChange={(evento) => {
                setPagina(1)
                setPrazoAte(evento.target.value)
              }}
              className="tabular-money"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={() => {
                setPagina(1)
                setPrazoAte(dataDaquiADias(DIAS_DE_ALERTA_DO_PRAZO))
              }}
            >
              Vencendo em 7 dias
            </Button>
          </div>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Multas de trânsito"
        // A fila de urgência abre pelo prazo mais apertado (na página carregada).
        ordenacaoInicial={[{ id: 'prazo', desc: false }]}
        estadoVazio={
          <EmptyState
            icone={ReceiptText}
            titulo={temFiltro ? 'Nenhuma multa com esses filtros' : 'Nenhuma multa registrada'}
            descricao={
              temFiltro
                ? 'Ajuste o status ou a data de prazo para ver outros resultados.'
                : 'Quando a notificação chegar do órgão, registre-a aqui: o sistema vincula a reserva pela placa e pelo período, e o prazo de indicação entra na fila de urgência.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button
                  onClick={() => {
                    setDialogoDeRegistro(true)
                  }}
                >
                  <Plus aria-hidden="true" />
                  Registrar multa
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeMulta
        aberto={dialogoDeRegistro}
        aoFechar={() => {
          setDialogoDeRegistro(false)
        }}
      />

      <DialogoDeIndicacaoDeCondutor
        aberto={multaParaIndicar !== null}
        multa={multaParaIndicar}
        aoFechar={() => {
          setMultaParaIndicar(null)
        }}
      />
    </div>
  )
}
